import { forwardRef } from "react";

import { styled, type Theme } from "@mui/material/styles";
import { Link, LinkProps, useLocation } from "react-router-dom";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  to?: never;
  keepSearchParams?: never;
};

// disabled/type are spelled out as never so the union discriminates: they are
// valid anchor attributes, so without them TypeScript can resolve <IconButton
// disabled type="button"> to the anchor branch and reject `disabled`.
type AnchorProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "type"
> & {
  to: LinkProps["to"];
  disabled?: never;
  keepSearchParams?: boolean;
  type?: never;
};

type Props = ButtonProps | AnchorProps;

const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (props, ref) => {
    if ("to" in props && props.to) {
      const { keepSearchParams, to, ...rest } = props as AnchorProps;
      const LinkComponent = keepSearchParams
        ? StyledSearchParamsLink
        : StyledLink;
      return (
        <LinkComponent
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          {...rest}
        />
      );
    }

    // type defaults to "button": HTML's own default is "submit", so an icon
    // button rendered inside a form would submit it on every click.
    const {
      keepSearchParams: _keepSearchParams,
      to: _to,
      type = "button",
      ...rest
    } = props as ButtonProps;
    return (
      <StyledButton
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        {...rest}
      />
    );
  },
);

const iconButtonStyles = ({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  padding: 0,
  margin: 0,
  border: "none",
  borderRadius: 8,
  background: "transparent",
  cursor: "pointer",
  color: theme.palette.cf.cfLightBlack,
  transition: "color 150ms ease",
  "& svg": {
    width: 20,
    height: 20,
  },
  "&:hover": {
    color: theme.palette.cf.cfPrimaryDarker,
  },
  "&:active": {
    color: theme.palette.cf.cfPrimaryDarkest,
  },
  "&:focus-visible": {
    outline: `1.5px dashed ${theme.palette.cf.cfPrimary}`,
    outlineOffset: 2,
    color: theme.palette.cf.cfPrimary,
  },
  "&:disabled": {
    color: theme.palette.cf.cfGrey,
    cursor: "default",
  },
});

const StyledButton = styled("button")(iconButtonStyles);

const StyledLink = styled(Link)(iconButtonStyles);

const SearchParamsLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, ...props }, ref) => {
    const { search } = useLocation();
    const resolvedTo =
      typeof to === "string"
        ? `${to}${search}`
        : { ...to, search: `${to.search ?? ""}${search}` };
    return <Link ref={ref} to={resolvedTo} {...props} />;
  },
);

const StyledSearchParamsLink = styled(SearchParamsLink)(iconButtonStyles);

export { IconButton };
